import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Star,
  StarHalf,
  MessageSquare,
  User,
  Stethoscope,
  ThumbsUp,
  ThumbsDown,
  Calendar,
  Loader2,
  ChevronRight,
  Award
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StarRating = ({ rating, size = 'md' }) => {
  const sizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };
  
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    if (i <= rating) {
      stars.push(<Star key={i} className={`${sizeClasses[size]} fill-yellow-400 text-yellow-400`} />);
    } else if (i - 0.5 <= rating) {
      stars.push(<StarHalf key={i} className={`${sizeClasses[size]} fill-yellow-400 text-yellow-400`} />);
    } else {
      stars.push(<Star key={i} className={`${sizeClasses[size]} text-gray-300`} />);
    }
  }
  
  return <div className="flex items-center gap-0.5">{stars}</div>;
};

export const ReviewsPage = () => {
  const { t } = useTranslation(['reviews', 'common']);
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    fetchReviews();
    fetchSummary();
  }, []);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/reviews?status=completed`);
      setReviews(response.data.data || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reviews/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const openDetail = (review) => {
    setSelectedReview(review);
    setDetailOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="reviews-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('title')}</h1>
          <p className="text-muted-foreground">{t('subtitle')}</p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('summary.totalReviews')}</p>
                  <p className="text-2xl font-bold">{summary.total_reviews}</p>
                </div>
                <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                  <MessageSquare className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('summary.averageRating')}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-bold">{summary.average_rating.toFixed(1)}</p>
                    <StarRating rating={summary.average_rating} size="md" />
                  </div>
                </div>
                <div className="p-3 rounded-full bg-yellow-100 dark:bg-yellow-900/30">
                  <Star className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('summary.responseRate')}</p>
                  <p className="text-2xl font-bold">{summary.response_rate}%</p>
                </div>
                <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/30">
                  <ThumbsUp className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-2">{t('summary.ratingDistribution')}</p>
              <div className="space-y-1">
                {[5, 4, 3, 2, 1].map(rating => {
                  const count = summary.rating_distribution[String(rating)] || 0;
                  const percentage = summary.total_reviews > 0 
                    ? (count / summary.total_reviews * 100) 
                    : 0;
                  
                  return (
                    <div key={rating} className="flex items-center gap-2">
                      <span className="text-xs w-3">{rating}</span>
                      <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                      <Progress value={percentage} className="h-2 flex-1" />
                      <span className="text-xs text-muted-foreground w-8">{count}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Specialists */}
      {summary?.top_specialists?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-yellow-500" />
              {t('summary.topSpecialists')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {summary.top_specialists.map((specialist, index) => (
                <div
                  key={specialist.specialist_id}
                  className="flex items-center gap-3 p-3 rounded-lg border bg-card"
                >
                  <div className={`flex items-center justify-center h-10 w-10 rounded-full ${
                    index === 0 ? 'bg-yellow-100 text-yellow-700' :
                    index === 1 ? 'bg-gray-100 text-gray-700' :
                    index === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    <span className="font-bold">{index + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{specialist.specialist_name}</p>
                    <div className="flex items-center gap-1">
                      <StarRating rating={specialist.average_rating} size="sm" />
                      <span className="text-xs text-muted-foreground">
                        ({specialist.total_reviews})
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reviews List */}
      <Card>
        <CardHeader>
          <CardTitle>{t('list.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('list.empty')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map(review => (
                <div
                  key={review.id}
                  className="flex items-start justify-between p-4 rounded-lg border bg-card hover:shadow-sm transition-shadow cursor-pointer"
                  onClick={() => openDetail(review)}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-full bg-primary/10">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{review.patient_name || 'Paciente'}</span>
                        <StarRating rating={review.overall_rating || 0} size="sm" />
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        <Stethoscope className="h-3 w-3 inline mr-1" />
                        {review.specialist_name || 'Especialista'}
                      </p>
                      {review.comment && (
                        <p className="text-sm mt-2 line-clamp-2">{review.comment}</p>
                      )}
                      {(review.positive_tags?.length > 0 || review.negative_tags?.length > 0) && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {review.positive_tags?.slice(0, 3).map((tag, i) => (
                            <Badge key={i} variant="secondary" className="text-xs bg-green-100 text-green-800">
                              <ThumbsUp className="h-3 w-3 mr-1" />
                              {tag}
                            </Badge>
                          ))}
                          {review.negative_tags?.slice(0, 2).map((tag, i) => (
                            <Badge key={i} variant="secondary" className="text-xs bg-red-100 text-red-800">
                              <ThumbsDown className="h-3 w-3 mr-1" />
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    {formatDate(review.collected_at)}
                    <ChevronRight className="h-4 w-4" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Detail Sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Detalle de Reseña</SheetTitle>
          </SheetHeader>
          {selectedReview && (
            <div className="mt-6 space-y-6">
              {/* Rating */}
              <div className="text-center">
                <div className="flex justify-center mb-2">
                  <StarRating rating={selectedReview.overall_rating || 0} size="lg" />
                </div>
                <p className="text-3xl font-bold">{selectedReview.overall_rating || 0}/5</p>
              </div>

              {/* Patient & Specialist */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground mb-1">{t('fields.patient')}</p>
                  <p className="font-medium">{selectedReview.patient_name}</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground mb-1">{t('fields.specialist')}</p>
                  <p className="font-medium">{selectedReview.specialist_name}</p>
                </div>
              </div>

              {/* Detailed Ratings */}
              {(selectedReview.punctuality_rating || selectedReview.professionalism_rating || selectedReview.service_quality_rating) && (
                <div className="space-y-3">
                  <h4 className="font-medium">Calificaciones Detalladas</h4>
                  {selectedReview.punctuality_rating && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm">{t('fields.punctualityRating')}</span>
                      <StarRating rating={selectedReview.punctuality_rating} size="sm" />
                    </div>
                  )}
                  {selectedReview.professionalism_rating && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm">{t('fields.professionalismRating')}</span>
                      <StarRating rating={selectedReview.professionalism_rating} size="sm" />
                    </div>
                  )}
                  {selectedReview.service_quality_rating && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm">{t('fields.serviceQualityRating')}</span>
                      <StarRating rating={selectedReview.service_quality_rating} size="sm" />
                    </div>
                  )}
                </div>
              )}

              {/* Comment */}
              {selectedReview.comment && (
                <div className="space-y-2">
                  <h4 className="font-medium">{t('fields.comment')}</h4>
                  <p className="text-sm p-4 rounded-lg bg-muted/50">{selectedReview.comment}</p>
                </div>
              )}

              {/* Tags */}
              {selectedReview.positive_tags?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-green-700">{t('tags.positive')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedReview.positive_tags.map((tag, i) => (
                      <Badge key={i} className="bg-green-100 text-green-800">
                        <ThumbsUp className="h-3 w-3 mr-1" />
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {selectedReview.negative_tags?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-red-700">{t('tags.negative')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedReview.negative_tags.map((tag, i) => (
                      <Badge key={i} className="bg-red-100 text-red-800">
                        <ThumbsDown className="h-3 w-3 mr-1" />
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Would Recommend */}
              {selectedReview.would_recommend !== null && selectedReview.would_recommend !== undefined && (
                <div className="flex items-center gap-2 p-4 rounded-lg bg-muted/50">
                  {selectedReview.would_recommend ? (
                    <>
                      <ThumbsUp className="h-5 w-5 text-green-600" />
                      <span className="text-green-700 font-medium">Recomendaría este especialista</span>
                    </>
                  ) : (
                    <>
                      <ThumbsDown className="h-5 w-5 text-red-600" />
                      <span className="text-red-700 font-medium">No recomendaría</span>
                    </>
                  )}
                </div>
              )}

              {/* Meta */}
              <div className="text-xs text-muted-foreground pt-4 border-t">
                <p>Recolectada: {formatDate(selectedReview.collected_at)}</p>
                <p>Canal: {selectedReview.collection_channel || 'WhatsApp'}</p>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ReviewsPage;
